import React, { useState, useEffect } from "react";

function getStepsForQuery(query = "") {
  const q = String(query || "").toLowerCase();

  if (
    q.includes("stock") ||
    q.includes("copper") ||
    q.includes("brass") ||
    q.includes("aluminum") ||
    q.includes("lot") ||
    q.includes("weight") ||
    q.includes("category") ||
    q.includes("tonnage")
  ) {
    return [
      { label: "Interpreting inventory inquiry & identifying scrap metal grades", minDuration: 4 },
      { label: "Connecting to live StockMaster & LotMaster tables", minDuration: 9 },
      { label: "Reconciling double-entry mass balance (Inward vs Deductions)", minDuration: 18 },
      { label: "Synthesizing grade metrics with NVIDIA Nemotron (120B)", minDuration: 32 },
      { label: "Structuring verified live stock summary & category cards", minDuration: 999 },
    ];
  }

  if (
    q.includes("shipment") ||
    q.includes("bl") ||
    q.includes("container") ||
    q.includes("port") ||
    q.includes("demurrage") ||
    q.includes("arrival")
  ) {
    return [
      { label: "Parsing maritime shipment logistics & container IDs", minDuration: 4 },
      { label: "Querying BL Master & container voyage records", minDuration: 9 },
      { label: "Auditing port free-day thresholds & demurrage exposure", minDuration: 18 },
      { label: "Cross-referencing ETA schedules with NVIDIA Nemotron AI", minDuration: 32 },
      { label: "Compiling verified shipment overview & logistics status", minDuration: 999 },
    ];
  }

  if (
    q.includes("bill") ||
    q.includes("invoice") ||
    q.includes("customer") ||
    q.includes("vendor") ||
    q.includes("supplier") ||
    q.includes("aging") ||
    q.includes("receivable") ||
    q.includes("payable") ||
    q.includes("overdue")
  ) {
    return [
      { label: "Parsing financial search criteria & counterparty names", minDuration: 4 },
      { label: "Scanning purchase bills, sales invoices & party directory", minDuration: 9 },
      { label: "Aggregating AP/AR aging buckets & currency balances", minDuration: 18 },
      { label: "Auditing commercial financial ledger with NVIDIA Nemotron", minDuration: 32 },
      { label: "Formatting verified financial aging breakdown & cards", minDuration: 999 },
    ];
  }

  return [
    { label: "Interpreting inquiry & analyzing enterprise business rules", minDuration: 4 },
    { label: "Scanning enterprise database & vector directory embeddings", minDuration: 9 },
    { label: "Executing verified ERP double-entry tool functions", minDuration: 18 },
    { label: "Engaging NVIDIA Nemotron (120B) for deep reasoning", minDuration: 32 },
    { label: "Synthesizing verified enterprise response & follow-ups", minDuration: 999 },
  ];
}

export default function ChatThinkingIndicator({ query }) {
  const [elapsed, setElapsed] = useState(0);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const steps = getStepsForQuery(query);

  let currentStepIndex = 0;
  for (let i = 0; i < steps.length; i++) {
    if (elapsed >= (steps[i - 1]?.minDuration || 0)) {
      currentStepIndex = i;
    }
  }

  const activeStep = steps[currentStepIndex] || steps[0];

  return (
    <div className="w-full max-w-[95%] rounded-2xl border border-slate-200/90 bg-white p-3.5 shadow-sm transition-all dark:border-slate-800 dark:bg-slate-900 rounded-bl-none">
      {/* Header with live pulsing beacon, title, timer & collapse toggle */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary-500" />
          </span>
          <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
            <span>AI Reasoning & ERP Retrieval</span>
            <span className="inline-flex items-center gap-0.5">
              <span className="inline-block h-1 w-1 rounded-full bg-primary-500 animate-bounce [animation-delay:-0.3s]" />
              <span className="inline-block h-1 w-1 rounded-full bg-primary-500 animate-bounce [animation-delay:-0.15s]" />
              <span className="inline-block h-1 w-1 rounded-full bg-primary-500 animate-bounce" />
            </span>
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Real-time elapsed timer badge */}
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-mono font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <svg className="h-3 w-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{elapsed}s</span>
          </span>

          {/* Toggle details chevron */}
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? "Collapse activity steps" : "Expand activity steps"}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300 transition"
          >
            <svg
              className={`h-3.5 w-3.5 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""
                }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Prominent Active Step Highlight */}
      <div className="mt-2 flex items-center gap-2 text-[11px] text-primary-700 dark:text-primary-300 font-medium bg-primary-50/70 dark:bg-primary-950/40 px-2.5 py-1.5 rounded-lg border border-primary-100 dark:border-primary-900/50">
        <svg
          className="h-3.5 w-3.5 animate-spin text-primary-500 shrink-0"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <span className="truncate">{activeStep.label}...</span>
      </div>

      {/* Step Progression Checklist */}
      {isExpanded && (
        <div className="mt-2.5 space-y-1.5 border-t border-slate-100 dark:border-slate-800/80 pt-2 text-[11px]">
          {steps.map((s, idx) => {
            const isCompleted = idx < currentStepIndex;
            const isCurrent = idx === currentStepIndex;
            return (
              <div
                key={idx}
                className={`flex items-center gap-2 transition-colors ${isCompleted
                    ? "text-slate-400 dark:text-slate-500"
                    : isCurrent
                      ? "font-medium text-slate-800 dark:text-slate-200"
                      : "text-slate-400/60 dark:text-slate-600"
                  }`}
              >
                {isCompleted ? (
                  <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400 text-[9px] font-bold shrink-0">
                    ✓
                  </span>
                ) : isCurrent ? (
                  <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary-100 text-primary-600 dark:bg-primary-950 dark:text-primary-400 shrink-0">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary-600 dark:bg-primary-400 animate-ping" />
                  </span>
                ) : (
                  <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-600 text-[9px] shrink-0">
                    •
                  </span>
                )}
                <span className="truncate">{s.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Subtle bottom animated hairline shimmer */}
      <div className="mt-2.5 h-0.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div className="h-full w-1/3 animate-pulse bg-gradient-to-r from-transparent via-primary-500 to-transparent" />
      </div>
    </div>
  );
}
